import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  schema?: object;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  schema,
}: SEOProps) => {
  useEffect(() => {
    // Set title
    document.title = title;

    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }

    // Set meta keywords
    if (keywords) {
      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', keywords);
      }
    }

    // Set canonical URL
    if (canonical) {
      let linkCanonical = document.querySelector('link[rel="canonical"]');
      if (!linkCanonical) {
        linkCanonical = document.createElement('link');
        linkCanonical.setAttribute('rel', 'canonical');
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.setAttribute('href', canonical);
    }

    // Set Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', title);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', description);
    }

    const ogTypeTag = document.querySelector('meta[property="og:type"]');
    if (ogTypeTag) {
      ogTypeTag.setAttribute('content', ogType);
    }

    // Set Twitter Card tags
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', title);
    }

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', description);
    }

    // Add Schema.org JSON-LD
    if (schema) {
      const scriptTag = document.createElement('script');
      scriptTag.type = 'application/ld+json';
      scriptTag.text = JSON.stringify(schema);
      scriptTag.id = 'page-schema';
      document.head.appendChild(scriptTag);

      return () => {
        const existingScript = document.getElementById('page-schema');
        if (existingScript) {
          document.head.removeChild(existingScript);
        }
      };
    }
  }, [title, description, keywords, canonical, ogType, schema]);
};

// Schema.org generators
export const generateWebPageSchema = (name: string, description: string, url: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name,
  description,
  url: `${import.meta.env.VITE_SITE_URL}${url}`,
});

export const generateArticleSchema = (
  headline: string,
  description: string,
  url: string,
  datePublished: string,
  dateModified: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline,
  description,
  url: `${import.meta.env.VITE_SITE_URL}${url}`,
  datePublished,
  dateModified,
  author: {
    '@type': 'Organization',
    name: 'cinewallkopen.nl',
  },
});

export const generateServiceSchema = (
  name: string,
  description: string,
  provider: string = 'cinewallkopen.nl'
) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  name,
  description,
  provider: {
    '@type': 'Organization',
    name: provider,
  },
  areaServed: {
    '@type': 'Country',
    name: 'Nederland',
  },
});

export const generateProductSchema = (
  name: string,
  description: string,
  priceRange: string
) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name,
  description,
  offers: {
    '@type': 'AggregateOffer',
    priceCurrency: 'EUR',
    price: priceRange,
  },
});
